function clamp(value, minimum = 0, maximum = 1) {
  return Math.max(minimum, Math.min(maximum, value));
}

function easeInOut(value) {
  const x = clamp(value, 0, 1);
  return x * x * (3 - 2 * x);
}

function pointOnPath(progress, width, height) {
  const t = clamp(progress, 0, 1);
  const inverse = 1 - t;
  const points = [
    { x: -0.12 * width, y: 0.72 * height },
    { x: 0.2 * width, y: 0.18 * height },
    { x: 0.72 * width, y: 0.86 * height },
    { x: 1.12 * width, y: 0.28 * height },
  ];
  return {
    x: inverse ** 3 * points[0].x + 3 * inverse ** 2 * t * points[1].x + 3 * inverse * t ** 2 * points[2].x + t ** 3 * points[3].x,
    y: inverse ** 3 * points[0].y + 3 * inverse ** 2 * t * points[1].y + 3 * inverse * t ** 2 * points[2].y + t ** 3 * points[3].y,
  };
}

const STRUCTURES = Object.freeze([
  { x: 0.03, y: 0.18, w: 0.18, h: 0.63, bevel: 0.018 },
  { x: 0.19, y: 0.31, w: 0.14, h: 0.5, bevel: 0.014 },
  { x: 0.7, y: 0.22, w: 0.14, h: 0.59, bevel: 0.016 },
  { x: 0.82, y: 0.35, w: 0.16, h: 0.46, bevel: 0.013 },
]);

export function createColdTrailCompositor(canvas, { still = false } = {}) {
  if (!(canvas instanceof HTMLCanvasElement)) throw new TypeError("A canvas is required.");
  const context = canvas.getContext("2d", { alpha: true, desynchronized: true });
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  let width = 0;
  let height = 0;
  let dpr = 1;
  let frame = 0;
  let startedAt = performance.now();
  let pausedAt = 0;
  let pausedDuration = 0;
  let manuallyPaused = false;
  let destroyed = false;

  function resize() {
    const bounds = canvas.getBoundingClientRect();
    width = Math.max(1, Math.round(bounds.width));
    height = Math.max(1, Math.round(bounds.height));
    dpr = Math.min(devicePixelRatio || 1, 1.5);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
    draw(still || reduced.matches ? 0.58 : currentProgress());
  }

  function currentProgress(time = performance.now()) {
    const elapsed = Math.max(0, time - startedAt - pausedDuration);
    const cycle = (elapsed % 13000) / 13000;
    if (cycle < 0.22) return 0.08;
    if (cycle < 0.68) return 0.08 + easeInOut((cycle - 0.22) / 0.46) * 0.84;
    if (cycle < 0.82) return 0.92;
    return 0.92 - easeInOut((cycle - 0.82) / 0.18) * 0.84;
  }

  function drawArchitecture(head) {
    const base = context.createLinearGradient(0, 0, 0, height);
    base.addColorStop(0, "#020405");
    base.addColorStop(0.55, "#05090b");
    base.addColorStop(1, "#010203");
    context.fillStyle = base;
    context.fillRect(0, 0, width, height);

    const light = context.createRadialGradient(head.x, head.y, 0, head.x, head.y, Math.max(width, height) * 0.34);
    light.addColorStop(0, "rgba(188,246,255,.18)");
    light.addColorStop(0.25, "rgba(77,185,213,.07)");
    light.addColorStop(1, "rgba(0,0,0,0)");
    context.fillStyle = light;
    context.fillRect(0, 0, width, height);

    STRUCTURES.forEach((block, index) => {
      const x = block.x * width;
      const y = block.y * height;
      const w = block.w * width;
      const h = block.h * height;
      const distance = Math.hypot(head.x - (x + w / 2), head.y - (y + h / 2));
      const response = clamp(1 - distance / (Math.max(width, height) * 0.38), 0, 1);
      const panel = context.createLinearGradient(x, y, x + w, y + h);
      panel.addColorStop(0, `rgba(${9 + response * 20},${14 + response * 27},${17 + response * 31},.92)`);
      panel.addColorStop(0.52, "rgba(3,6,8,.96)");
      panel.addColorStop(1, "rgba(1,3,4,.98)");
      context.beginPath();
      context.moveTo(x + block.bevel * width, y);
      context.lineTo(x + w, y);
      context.lineTo(x + w, y + h - block.bevel * width);
      context.lineTo(x + w - block.bevel * width, y + h);
      context.lineTo(x, y + h);
      context.lineTo(x, y + block.bevel * width);
      context.closePath();
      context.fillStyle = panel;
      context.fill();
      context.strokeStyle = `rgba(118,168,180,${0.08 + response * 0.3})`;
      context.lineWidth = 1;
      context.stroke();

      context.beginPath();
      context.moveTo(x + w * 0.14, y + h * 0.18);
      context.lineTo(x + w * 0.86, y + h * 0.18);
      context.moveTo(x + w * 0.14, y + h * 0.74);
      context.lineTo(x + w * (index % 2 ? 0.64 : 0.78), y + h * 0.74);
      context.strokeStyle = `rgba(168,224,234,${0.04 + response * 0.23})`;
      context.stroke();
    });

    context.strokeStyle = "rgba(103,139,150,.08)";
    context.lineWidth = 1;
    for (let i = 0; i < 6; i += 1) {
      const y = height * (0.17 + i * 0.13);
      context.beginPath();
      context.moveTo(width * 0.26, y);
      context.lineTo(width * 0.68, y + (i % 2 ? 8 : -5));
      context.stroke();
    }
  }

  function drawTrail(progress) {
    const head = pointOnPath(progress, width, height);
    const trailLength = 0.22;
    const samples = 76;
    const paths = [
      { width: 18, alpha: 0.07, blur: 24, color: "111,225,255" },
      { width: 7, alpha: 0.19, blur: 13, color: "102,220,247" },
      { width: 2.2, alpha: 0.88, blur: 4, color: "218,251,255" },
      { width: 0.8, alpha: 1, blur: 0, color: "255,255,255" },
    ];
    paths.forEach((layer) => {
      context.save();
      context.lineCap = "round";
      context.lineJoin = "round";
      context.lineWidth = layer.width;
      context.shadowBlur = layer.blur;
      context.shadowColor = `rgba(${layer.color},${layer.alpha})`;
      context.beginPath();
      for (let i = 0; i < samples; i += 1) {
        const age = i / (samples - 1);
        const t = clamp(progress - trailLength + trailLength * age, 0, 1);
        const point = pointOnPath(t, width, height);
        const jitter = Math.sin(i * 2.17 + progress * 31) * (1 - age) * 1.4;
        if (i === 0) context.moveTo(point.x, point.y + jitter);
        else context.lineTo(point.x, point.y + jitter);
      }
      context.strokeStyle = `rgba(${layer.color},${layer.alpha})`;
      context.stroke();
      context.restore();
    });

    for (let i = 0; i < 11; i += 1) {
      const phase = (i * 0.091 + progress * 1.31) % 1;
      const point = pointOnPath(clamp(progress - phase * trailLength, 0, 1), width, height);
      const radius = 0.7 + (i % 3) * 0.45;
      context.beginPath();
      context.arc(point.x + Math.sin(i * 4.1) * 8, point.y + Math.cos(i * 3.3) * 6, radius, 0, Math.PI * 2);
      context.fillStyle = `rgba(176,239,255,${0.12 + (1 - phase) * 0.3})`;
      context.fill();
    }

    const headGlow = context.createRadialGradient(head.x, head.y, 0, head.x, head.y, 48);
    headGlow.addColorStop(0, "rgba(255,255,255,1)");
    headGlow.addColorStop(0.08, "rgba(218,251,255,.95)");
    headGlow.addColorStop(0.3, "rgba(95,219,250,.34)");
    headGlow.addColorStop(1, "rgba(31,144,184,0)");
    context.fillStyle = headGlow;
    context.fillRect(head.x - 50, head.y - 50, 100, 100);
    return head;
  }

  function draw(progress) {
    if (destroyed || !width || !height) return;
    const head = pointOnPath(progress, width, height);
    context.clearRect(0, 0, width, height);
    drawArchitecture(head);
    drawTrail(progress);
    canvas.dataset.trailProgress = progress.toFixed(3);
  }

  function tick(time) {
    frame = 0;
    if (destroyed || manuallyPaused || document.hidden || still || reduced.matches) return;
    draw(currentProgress(time));
    frame = requestAnimationFrame(tick);
  }

  function start() {
    if (destroyed || frame || manuallyPaused || document.hidden || still || reduced.matches) return;
    if (pausedAt) {
      pausedDuration += performance.now() - pausedAt;
      pausedAt = 0;
    }
    frame = requestAnimationFrame(tick);
  }

  function stop() {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    if (!pausedAt) pausedAt = performance.now();
  }

  function onVisibility() {
    if (document.hidden) stop();
    else start();
  }

  function onMotionChange() {
    if (reduced.matches) {
      stop();
      draw(0.58);
    } else start();
  }

  const observer = new ResizeObserver(resize);
  observer.observe(canvas);
  document.addEventListener("visibilitychange", onVisibility);
  reduced.addEventListener("change", onMotionChange);
  resize();
  start();

  return Object.freeze({
    toggle() {
      manuallyPaused = !manuallyPaused;
      if (manuallyPaused) stop();
      else start();
      canvas.dataset.motion = manuallyPaused ? "paused" : still || reduced.matches ? "static" : "running";
      return !manuallyPaused;
    },
    getState() {
      return { renderer: "canvas-2d", motion: manuallyPaused ? "paused" : still || reduced.matches ? "static" : "running", mutation: false };
    },
    destroy() {
      destroyed = true;
      stop();
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      reduced.removeEventListener("change", onMotionChange);
    },
  });
}

